import {
  type HotelInventoryListItem,
  type ItineraryComponentOut,
  type TransferMode,
  type UpdateItineraryComponentRequest,
  isPublicTransferMode,
} from '@utazzo/shared';
import { UzBadge, UzButton, UzSelect } from '@utazzo/ui';
import {
  AlertTriangle,
  ArrowRight,
  BedDouble,
  Bus,
  CalendarDays,
  Car,
  Check,
  Clock,
  LogIn,
  LogOut,
  Loader2,
  MapPin,
  Pencil,
  Plane,
  Ship,
  Sparkles,
  Star,
  Ticket,
  TrainFront,
  Trash2,
  Users,
  Utensils,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { paxRoomsForStay, roomsForStayFull, stayRoomsAreGlobal } from '../stay-rooms';
import { useItineraryDetail, useTboHotelLiveDetails, useTboHotelRooms } from '../use-itineraries';

import { MealBadge } from './MealBadge';
import { priceInventoryForConfig } from './preferred-hotel-pricing';
import { AdminOnlyAmount, CardPrice, PriceDelta, usePriceVisibility } from './price-visibility';

import { useCruiseCoverUrl } from '@/features/cruises/use-cruise-cover';
import { useCruiseDetail } from '@/features/cruises/use-cruises';
import { useHotelInventory, useHotelRooms } from '@/features/hotels/use-hotels';
import { formatDMY } from '@/lib/date-format';

/**
 * Rich day-component cards (Phase 4a). FLIGHT and HOTEL render detailed cards
 * built from the component fields + metaJson the search/auto-search stored
 * (flight segments, star rating, dates). Everything else falls back to a compact
 * row. Flight "Details" opens a popup with the full timeline + layovers.
 */

/**
 * The fixed-departure "Land package" VAS carries the whole trip cost but is not
 * a real day component - it is hidden from the day plan and preview so the trip
 * does not read as double-billed. Its cost still flows through the totals.
 *
 * Prefer the durable `metaJson.fdLandPackage` marker set at seed time; fall back
 * to the title for itineraries seeded before the marker existed.
 */
export const isFdLandPackage = (c: ItineraryComponentOut): boolean => {
  if (c.kind !== 'VAS') return false;
  const meta = c.metaJson as { fdLandPackage?: boolean } | null | undefined;
  return meta?.fdLandPackage === true || (c.title ?? '').startsWith('Land package');
};

/**
 * A fixed-departure vehicle supplement, seeded as an unscheduled TRANSFER with
 * the `fdSupplementLabel` marker. It gets its own read-only "Vehicle
 * supplements" section on the Days tab - selection/quantity is managed from the
 * Configure step, not here.
 */
export const isFdSupplement = (c: ItineraryComponentOut): boolean => {
  if (c.kind !== 'TRANSFER') return false;
  const meta = c.metaJson as { fdSupplementLabel?: string } | null | undefined;
  return typeof meta?.fdSupplementLabel === 'string';
};

/**
 * A fixed-departure BLOCK flight, seeded as a FLIGHT (source DB) with the
 * `fdBlockFlight` marker. It renders on its day like any flight but is read-only
 * here - the selection is managed on the Configure step, not on the day plan.
 */
export const isFdBlockFlight = (c: ItineraryComponentOut): boolean => {
  if (c.kind !== 'FLIGHT') return false;
  const meta = c.metaJson as { fdBlockFlight?: boolean } | null | undefined;
  return meta?.fdBlockFlight === true;
};

interface Segment {
  airline: string;
  airlineName?: string | null;
  flightNumber: string;
  origin: string;
  originAirport?: string | null;
  originCity?: string | null;
  originTerminal?: string | null;
  destination: string;
  destinationAirport?: string | null;
  destinationCity?: string | null;
  destinationTerminal?: string | null;
  departureAt: string;
  arrivalAt: string;
  durationMinutes: number;
  cabinClass?: string;
  baggageAllowance: string | null;
}

const KIND_META: Record<string, { label: string; Icon: typeof Plane; tone: string }> = {
  FLIGHT: { label: 'Flight', Icon: Plane, tone: 'text-sky-600 bg-sky-50' },
  HOTEL: { label: 'Hotel', Icon: BedDouble, tone: 'text-violet-600 bg-violet-50' },
  ACTIVITY: { label: 'Activity', Icon: Ticket, tone: 'text-amber-600 bg-amber-50' },
  TRANSFER: { label: 'Transfer', Icon: Car, tone: 'text-emerald-600 bg-emerald-50' },
  VAS: { label: 'Service', Icon: Sparkles, tone: 'text-rose-600 bg-rose-50' },
  // Cruise-as-stay (Phase 2). Cyan reads distinct from Flight's sky and the
  // point-to-point CRUISE transfer mode uses the same Ship glyph elsewhere.
  CRUISE: { label: 'Cruise', Icon: Ship, tone: 'text-cyan-600 bg-cyan-50' },
};

/**
 * Per-mode transfer icon + label. CAR = per-vehicle; BUS/TRAIN/FERRY are
 * public per-person modes (priced by a fare class / cabin, no vehicle). FERRY
 * is the point-to-point sea crossing, not a multi-night cruise stay.
 */
const TRANSFER_MODE_META: Record<TransferMode, { label: string; Icon: typeof Car }> = {
  CAR: { label: 'Car', Icon: Car },
  BUS: { label: 'Bus', Icon: Bus },
  TRAIN: { label: 'Train', Icon: TrainFront },
  FERRY: { label: 'Ferry', Icon: Ship },
};

const airlineLogo = (iata: string): string => `https://pics.avs.io/120/40/${iata}.png`;

/** Common carrier names (code -> brand) so the card reads like a real OTA, not just "6E". */
const AIRLINE_NAMES: Record<string, string> = {
  '6E': 'IndiGo',
  AI: 'Air India',
  UK: 'Vistara',
  SG: 'SpiceJet',
  QP: 'Akasa Air',
  G8: 'Go First',
  I5: 'AIX Connect',
  VN: 'Vietnam Airlines',
  VJ: 'VietJet Air',
  QH: 'Bamboo Airways',
  TG: 'Thai Airways',
  FD: 'Thai AirAsia',
  SL: 'Thai Lion Air',
  SQ: 'Singapore Airlines',
  TR: 'Scoot',
  MI: 'SilkAir',
  EK: 'Emirates',
  EY: 'Etihad Airways',
  QR: 'Qatar Airways',
  FZ: 'flydubai',
  CX: 'Cathay Pacific',
  MH: 'Malaysia Airlines',
  AK: 'AirAsia',
  BR: 'EVA Air',
  CI: 'China Airlines',
  KE: 'Korean Air',
  OZ: 'Asiana Airlines',
  NH: 'ANA',
  JL: 'Japan Airlines',
  GA: 'Garuda Indonesia',
  PR: 'Philippine Airlines',
  UL: 'SriLankan Airlines',
  BG: 'Biman Bangladesh',
  TK: 'Turkish Airlines',
  LH: 'Lufthansa',
  BA: 'British Airways',
  AF: 'Air France',
  KL: 'KLM',
};
const airlineName = (code?: string | null): string =>
  code ? (AIRLINE_NAMES[code.toUpperCase()] ?? code.toUpperCase()) : 'Flight';

/** Big, unboxed airline logo with a Plane fallback when the CDN has no logo. */
const AirlineLogo = ({
  code,
  size = 'md',
}: {
  code?: string;
  size?: 'sm' | 'md';
}): React.JSX.Element => {
  const cls = size === 'sm' ? 'h-5 max-w-[40px]' : 'h-8 max-w-[56px]';
  return (
    <span className="inline-flex shrink-0 items-center justify-center">
      {code ? (
        <img
          src={airlineLogo(code)}
          alt={airlineName(code)}
          className={`${cls} w-auto object-contain`}
          onError={(e) => {
            const el = e.currentTarget as HTMLImageElement;
            el.style.display = 'none';
            el.nextElementSibling?.classList.remove('hidden');
          }}
        />
      ) : null}
      <Plane
        className={`${size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} text-sky-500 ${code ? 'hidden' : ''}`}
        aria-hidden
      />
    </span>
  );
};

const fmtTime = (s?: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) {
    return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  }
  const m = s.match(/(\d{2}:\d{2})/);
  return m?.[1] ?? s;
};

const fmtDateShort = (s?: string | null): string => {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime())
    ? s
    : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
};

/** Add `n` days to a YYYY-MM-DD string, UTC-based so it never drifts a day
 *  across timezones. Mirrors the UTC parsing pattern in `@/lib/date-format`. */
const addDaysYmd = (ymd: string, n: number): string => {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + n);
  const yyyy = d.getUTCFullYear();
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const fmtDuration = (min: number): string => {
  if (!min || min < 0) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}h${m ? ` ${m}m` : ''}`;
};

const readMeta = (c: ItineraryComponentOut): Record<string, unknown> =>
  c.metaJson && typeof c.metaJson === 'object' ? (c.metaJson as Record<string, unknown>) : {};

const readSegments = (v: unknown): Segment[] => (Array.isArray(v) ? (v as Segment[]) : []);

const titleCase = (s: string): string =>
  s
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const Dot = (): React.JSX.Element => (
  <span className="text-zinc-300" aria-hidden>
    ·
  </span>
);

interface ComponentCardProps {
  component: ItineraryComponentOut;
  editable: boolean;
  onEdit: () => void;
  onRemove: () => void;
  /** Patch the component in place (used by the hotel rooms/meal editor). */
  onUpdate?: (patch: Partial<UpdateItineraryComponentRequest>) => void;
  /** Itinerary id - lets the hotel editor fetch live TBO room options. */
  itineraryId?: string;
  /** Day context: the cities this component travels between (for the flight card). */
  fromCityName?: string | null;
  toCityName?: string | null;
  /** Trailing action (e.g. an "Add" button) - used to reuse the card in search popups. */
  actionSlot?: React.ReactNode;
  /** Hotel card fetches live photo/address. Off for search rows (avoids N fetches). */
  fetchLiveDetails?: boolean;
  /** True on long-haul destinations. Activity + Transfer rows use this to
   *  swap the price cell for an inline "Save net price" editor when
   *  `costPaise === 0` (the master carried a zero rate). Never shown on
   *  hotels / flights / VAS or on non-long-haul destinations. */
  isLongHaul?: boolean;
  /** Persist a manually-entered net price on the component. Only invoked
   *  from the long-haul inline editor. */
  onManualPriceSave?: (componentId: string, newCostPaise: string) => void | Promise<void>;
  /** Cruise card only: "Change cabin & meals" - change cabin/meal plan WITHIN
   *  the already-selected cruise. Falls back to `onEdit` ("Change cruise",
   *  the full cruise list) when not supplied. */
  onChangeCabin?: () => void;
  /** The day this component sits on (ISO YYYY-MM-DD). Transfer row uses it to
   *  compute + show a multi-day pass's validity window (start to auto end date). */
  dayDate?: string | null;
  /** The stay's city id - lets the hotel card resolve a per-stay room override
   *  (else the itinerary's global room config) for its auto-pick + rooms editor. */
  stayCityId?: string | null;
}

export const ComponentCard = ({
  component,
  editable,
  onEdit,
  onRemove,
  onUpdate,
  itineraryId,
  fromCityName,
  toCityName,
  actionSlot,
  fetchLiveDetails = true,
  isLongHaul = false,
  onManualPriceSave,
  onChangeCabin,
  dayDate,
  stayCityId,
}: ComponentCardProps): React.JSX.Element => {
  if (component.kind === 'FLIGHT') {
    return (
      <FlightCard
        component={component}
        editable={editable}
        onEdit={onEdit}
        onRemove={onRemove}
        fromCityName={fromCityName}
        toCityName={toCityName}
        actionSlot={actionSlot}
      />
    );
  }
  if (component.kind === 'HOTEL') {
    return (
      <HotelCard
        component={component}
        editable={editable}
        onEdit={onEdit}
        onRemove={onRemove}
        onUpdate={onUpdate}
        itineraryId={itineraryId}
        stayCityId={stayCityId}
        actionSlot={actionSlot}
        fetchLiveDetails={fetchLiveDetails}
      />
    );
  }
  if (component.kind === 'ACTIVITY') {
    return (
      <ActivityRow
        component={component}
        editable={editable}
        onEdit={onEdit}
        onRemove={onRemove}
        isLongHaul={isLongHaul}
        onManualPriceSave={onManualPriceSave}
      />
    );
  }
  if (component.kind === 'TRANSFER') {
    return (
      <TransferRow
        component={component}
        editable={editable}
        onEdit={onEdit}
        onRemove={onRemove}
        isLongHaul={isLongHaul}
        onManualPriceSave={onManualPriceSave}
        dayDate={dayDate}
      />
    );
  }
  if (component.kind === 'CRUISE') {
    return (
      <CruiseCard
        component={component}
        editable={editable}
        onEdit={onEdit}
        onRemove={onRemove}
        actionSlot={actionSlot}
        // No isLongHaul: cruises are always open-box (manual price editable),
        // independent of the destination's haul. See the CruiseCard price cell.
        onManualPriceSave={onManualPriceSave}
        onChangeCabin={onChangeCabin}
      />
    );
  }
  return (
    <GenericRow component={component} editable={editable} onEdit={onEdit} onRemove={onRemove} />
  );
};

// ---- Inter-city travel divider (Phase 4b) ----

export const TravelCard = ({
  fromCity,
  toCity,
  date,
  flight,
  transfer,
}: {
  fromCity: string;
  toCity: string;
  date?: string | null;
  flight?: ItineraryComponentOut;
  /** The leg's placed city-to-city transfer, when there's no flight. Drives the
   *  mode-aware icon + label (Train / Bus / Cruise / Drive). */
  transfer?: ItineraryComponentOut | null;
}): React.JSX.Element => {
  const meta = flight ? readMeta(flight) : {};
  const segs = readSegments(meta.outbound);
  const first = segs[0];
  const last = segs[segs.length - 1];
  const byFlight = Boolean(flight);
  const carrier = first ? `${first.airline} ${first.flightNumber}` : null;
  // Mode of the surface leg (only when not flying). Car is the default.
  const legMode: TransferMode = (() => {
    const m = transfer ? readMeta(transfer).transferMode : null;
    return m === 'BUS' || m === 'TRAIN' || m === 'FERRY' ? m : 'CAR';
  })();
  const LegIcon = TRANSFER_MODE_META[legMode].Icon;
  const surfaceLabel =
    legMode === 'TRAIN'
      ? 'Train transfer'
      : legMode === 'BUS'
        ? 'Bus transfer'
        : legMode === 'FERRY'
          ? 'Ferry transfer'
          : 'Surface transfer';
  return (
    <div className="relative flex items-center gap-3 rounded-xl border border-zinc-200 bg-zinc-50/80 px-4 py-2.5">
      <span
        className={
          'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full ring-2 ring-white ' +
          (byFlight ? 'bg-sky-100 text-sky-600' : 'bg-emerald-100 text-emerald-600')
        }
      >
        {byFlight ? (
          <Plane className="h-4 w-4 rotate-90" aria-hidden />
        ) : (
          <LegIcon className="h-4 w-4" aria-hidden />
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="flex items-center gap-1.5 text-sm font-semibold text-zinc-800">
          <span className="truncate">{fromCity}</span>
          <ArrowRight className="h-3.5 w-3.5 shrink-0 text-zinc-400" aria-hidden />
          <span className="truncate">{toCity}</span>
        </p>
        <p className="flex flex-wrap items-center gap-x-1.5 text-[11px] text-zinc-500">
          <span>{byFlight ? 'Check-out, transfer & fly' : surfaceLabel}</span>
          {date && (
            <>
              <Dot />
              <span>{date}</span>
            </>
          )}
          {carrier && (
            <>
              <Dot />
              <span className="font-medium text-zinc-600">{carrier}</span>
            </>
          )}
        </p>
      </div>
      {first && (
        <span className="shrink-0 text-right text-[11px] text-zinc-500">
          <span className="font-semibold text-zinc-700 tabular-nums">
            {fmtTime(first.departureAt)} - {fmtTime(last?.arrivalAt)}
          </span>
          <br />
          <span className="tabular-nums">
            {first.origin} - {last?.destination}
          </span>
        </span>
      )}
    </div>
  );
};

// ---- Flight ----

const FlightCard = ({
  component,
  editable,
  onEdit,
  onRemove,
  fromCityName,
  toCityName,
  actionSlot,
}: ComponentCardProps): React.JSX.Element => {
  const [open, setOpen] = useState(false);
  const meta = readMeta(component);
  const currency = typeof meta.currencyCode === 'string' ? meta.currencyCode : 'INR';
  const outbound = readSegments(meta.outbound);
  const returns = readSegments(meta.returns);

  const first = outbound[0];
  const last = outbound[outbound.length - 1];
  const from = first?.origin ?? component.fromLocation ?? '';
  const to = last?.destination ?? component.toLocation ?? '';
  const stops = Math.max(0, outbound.length - 1);
  const totalMin = outbound.reduce((s, seg) => s + (seg.durationMinutes || 0), 0);
  const cabin = first?.cabinClass ? titleCase(first.cabinClass) : null;
  const bag = first?.baggageAllowance ?? null;
  const carrier = first?.airlineName || airlineName(first?.airline);
  const fromCity = first?.originCity || fromCityName || null;
  const toCity = last?.destinationCity || toCityName || null;
  const hasReturn = returns.length > 0;
  const isBundle = meta.bundle === true;
  const bundleReturn = meta.includedInBundle === true;
  const overnight =
    first && last && fmtDateShort(first.departureAt) !== fmtDateShort(last.arrivalAt);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-2.5 transition-shadow hover:shadow-sm">
      {/* Carrier + fare */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2">
          <AirlineLogo code={first?.airline} />
          <div className="min-w-0">
            <p className="truncate text-[14px] font-semibold text-zinc-900">
              {carrier || 'Flight'}
            </p>
            <p className="truncate text-[12px] text-zinc-500">
              {/* Airline + flight number are optional (block flights). When both
                  are absent show nothing rather than a stray " · " / empty gap. */}
              {[
                first
                  ? [first.airline, first.flightNumber].filter(Boolean).join(' ')
                  : component.title,
                cabin,
              ]
                .filter(Boolean)
                .join(' · ') || ' '}
            </p>
          </div>
        </div>
        <div className="shrink-0 text-right">
          {bundleReturn ? (
            <p className="rounded bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700">
              Included in round-trip
            </p>
          ) : (
            <CardPrice
              componentId={component.id}
              costPaise={component.costPaise}
              currency={currency}
              amountClassName="block text-[17px] leading-none font-bold text-zinc-900 tabular-nums"
              caption={`total fare${isBundle || hasReturn ? ' · round trip' : ''}`}
            />
          )}
        </div>
      </div>

      {/* Route timeline */}
      {first ? (
        <div className="mt-2 flex items-stretch gap-3">
          <div className="shrink-0 text-left">
            <p className="text-[16px] leading-none font-semibold text-zinc-900 tabular-nums">
              {fmtTime(first.departureAt)}
            </p>
            <p className="mt-0.5 text-[14px] font-medium text-zinc-700">{from}</p>
            {fromCity && (
              <p className="max-w-[110px] truncate text-[11px] text-zinc-400">{fromCity}</p>
            )}
            <p className="text-[11px] text-zinc-400">{fmtDateShort(first.departureAt)}</p>
          </div>

          <div className="flex min-w-0 flex-1 flex-col items-center justify-start gap-0.5 pt-0.5">
            <span className="text-[11px] text-zinc-500">{fmtDuration(totalMin)}</span>
            <div className="flex w-full items-center gap-1">
              <span className="h-2 w-2 shrink-0 rounded-full border-2 border-sky-400" />
              <span className="h-px flex-1 bg-zinc-200" />
              <Plane className="h-3.5 w-3.5 shrink-0 rotate-90 text-sky-500" aria-hidden />
              <span className="h-px flex-1 bg-zinc-200" />
              <span className="h-2 w-2 shrink-0 rounded-full border-2 border-sky-400" />
            </div>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="hover:text-primary inline-flex items-center gap-1 text-[11px] font-medium text-zinc-500 underline-offset-2 hover:underline"
            >
              {stops === 0 ? 'Non-stop' : `${stops} stop${stops > 1 ? 's' : ''}`}
              <ArrowRight className="h-2.5 w-2.5" aria-hidden />
              <span className="text-zinc-400">{stops === 0 ? 'view' : 'layover'}</span>
            </button>
          </div>

          <div className="shrink-0 text-right">
            <p className="text-[16px] leading-none font-semibold text-zinc-900 tabular-nums">
              {fmtTime(last?.arrivalAt)}
              {overnight && <span className="ml-0.5 align-top text-[11px] text-amber-600">+1</span>}
            </p>
            <p className="mt-0.5 text-[14px] font-medium text-zinc-700">{to}</p>
            {toCity && (
              <p className="ml-auto max-w-[110px] truncate text-[11px] text-zinc-400">{toCity}</p>
            )}
            <p className="text-[11px] text-zinc-400">{fmtDateShort(last?.arrivalAt)}</p>
          </div>
        </div>
      ) : (
        <p className="mt-2 text-[15px] text-zinc-600">{component.title}</p>
      )}

      {/* Meta + actions */}
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 pt-2">
        <div className="flex flex-wrap items-center gap-1.5 text-[13px] text-zinc-500">
          {bag && (
            <span className="inline-flex items-center gap-1 rounded bg-zinc-100 px-1.5 py-0.5 text-[12px] text-zinc-600">
              {bag} baggage
            </span>
          )}
          {component.isRefundable ? (
            <UzBadge tone="success">Refundable</UzBadge>
          ) : (
            <UzBadge tone="secondary">Non-refundable</UzBadge>
          )}
        </div>
        <div className="flex items-center gap-2">
          {editable && (
            <EditRemove
              onEdit={onEdit}
              onRemove={onRemove}
              title={component.title}
              kind={component.kind}
            />
          )}
          {actionSlot}
        </div>
      </div>

      {open && (
        <FlightDetailDialog
          carrier={carrier}
          from={from}
          to={to}
          fromCityName={fromCity}
          toCityName={toCity}
          outbound={outbound}
          returns={returns}
          onClose={() => setOpen(false)}
        />
      )}
    </div>
  );
};

const FlightDetailDialog = ({
  carrier,
  from,
  to,
  fromCityName,
  toCityName,
  outbound,
  returns,
  onClose,
}: {
  carrier: string;
  from: string;
  to: string;
  fromCityName?: string | null;
  toCityName?: string | null;
  outbound: Segment[];
  returns: Segment[];
  onClose: () => void;
}): React.JSX.Element => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="max-h-[82vh] w-full max-w-lg overflow-auto rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <AirlineLogo code={outbound[0]?.airline} size="sm" />
            <div>
              <h3 className="flex items-center gap-1.5 text-[15px] font-semibold tracking-tight text-zinc-900">
                {fromCityName ?? from}
                <ArrowRight className="h-3.5 w-3.5 text-zinc-400" aria-hidden />
                {toCityName ?? to}
              </h3>
              <p className="text-[12px] text-zinc-500">{carrier} · flight details</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="space-y-5 px-5 py-5">
          <FlightLeg label="Outbound" segments={outbound} />
          {returns.length > 0 && <FlightLeg label="Return" segments={returns} />}
        </div>
      </div>
    </div>
  );
};

const FlightLeg = ({
  label,
  segments,
}: {
  label: string;
  segments: Segment[];
}): React.JSX.Element => (
  <div>
    <p className="mb-2.5 text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
      {label}
    </p>
    <ol className="relative space-y-0">
      {segments.map((seg, i) => {
        const prev = segments[i - 1];
        const layoverMin =
          prev && seg.departureAt && prev.arrivalAt
            ? Math.max(
                0,
                Math.round(
                  (new Date(seg.departureAt).getTime() - new Date(prev.arrivalAt).getTime()) /
                    60000,
                ),
              )
            : 0;
        return (
          <li key={i}>
            {layoverMin > 0 && (
              <div className="my-1.5 flex items-center gap-2">
                <span className="h-px flex-1 bg-amber-200" />
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-[12px] font-medium text-amber-700">
                  <Clock className="h-3 w-3" aria-hidden />
                  {fmtDuration(layoverMin)} layover · {prev?.destinationCity || prev?.destination}
                </span>
                <span className="h-px flex-1 bg-amber-200" />
              </div>
            )}
            <div className="rounded-lg border border-zinc-200 bg-white p-3">
              {segments.length > 1 && (
                <p className="mb-2 text-[11px] font-semibold tracking-wider text-zinc-400 uppercase">
                  Flight {i + 1} · {seg.originCity || seg.origin} -{' '}
                  {seg.destinationCity || seg.destination}
                </p>
              )}
              <div className="flex items-center gap-3">
                <AirlineLogo code={seg.airline} size="sm" />
                <div className="flex flex-1 items-start gap-2.5 text-[15px]">
                  <div className="min-w-0">
                    <p className="font-semibold tabular-nums">{fmtTime(seg.departureAt)}</p>
                    <p className="text-[12px] font-medium text-zinc-600">
                      {seg.originCity ? `${seg.originCity} (${seg.origin})` : seg.origin}
                    </p>
                    {seg.originAirport && (
                      <p className="max-w-[120px] truncate text-[11px] text-zinc-400">
                        {seg.originAirport}
                        {seg.originTerminal ? ` · T${seg.originTerminal}` : ''}
                      </p>
                    )}
                    <p className="text-[11px] text-zinc-400">{fmtDateShort(seg.departureAt)}</p>
                  </div>
                  <div className="flex flex-1 flex-col items-center pt-1">
                    <span className="text-[11px] text-zinc-400">
                      {fmtDuration(seg.durationMinutes)}
                    </span>
                    <div className="flex w-full items-center gap-0.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
                      <span className="h-px flex-1 bg-zinc-200" />
                      <Plane className="h-3 w-3 rotate-90 text-sky-400" aria-hidden />
                      <span className="h-px flex-1 bg-zinc-200" />
                      <span className="h-1.5 w-1.5 rounded-full bg-zinc-300" />
                    </div>
                  </div>
                  <div className="min-w-0 text-right">
                    <p className="font-semibold tabular-nums">{fmtTime(seg.arrivalAt)}</p>
                    <p className="text-[12px] font-medium text-zinc-600">
                      {seg.destinationCity
                        ? `${seg.destinationCity} (${seg.destination})`
                        : seg.destination}
                    </p>
                    {seg.destinationAirport && (
                      <p className="ml-auto max-w-[120px] truncate text-[11px] text-zinc-400">
                        {seg.destinationAirport}
                        {seg.destinationTerminal ? ` · T${seg.destinationTerminal}` : ''}
                      </p>
                    )}
                    <p className="text-[11px] text-zinc-400">{fmtDateShort(seg.arrivalAt)}</p>
                  </div>
                </div>
                <div className="shrink-0 text-right text-[12px] text-zinc-500">
                  <p className="font-medium text-zinc-600">
                    {airlineName(seg.airline)} {seg.flightNumber}
                  </p>
                  {seg.cabinClass && <p>{titleCase(seg.cabinClass)}</p>}
                  {seg.baggageAllowance && <p>Bag: {seg.baggageAllowance}</p>}
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  </div>
);

// ---- Hotel ----

const HotelCard = ({
  component,
  editable,
  onEdit,
  onRemove,
  onUpdate,
  itineraryId,
  stayCityId,
  actionSlot,
  fetchLiveDetails = true,
}: ComponentCardProps): React.JSX.Element => {
  const [editRooms, setEditRooms] = useState(false);
  const meta = readMeta(component);
  const currency = typeof meta.currencyCode === 'string' ? meta.currencyCode : 'INR';
  const metaStars = typeof meta.starRating === 'number' ? meta.starRating : null;
  const nights = typeof meta.nights === 'number' ? meta.nights : null;
  const extraMattress = typeof meta.extraMattress === 'number' ? meta.extraMattress : 0;
  const extraAdult = typeof meta.extraAdult === 'number' ? meta.extraAdult : 0;
  const checkIn = component.startDate ?? (typeof meta.checkIn === 'string' ? meta.checkIn : null);
  const checkOut = component.endDate ?? (typeof meta.checkOut === 'string' ? meta.checkOut : null);
  const checkInTime = typeof meta.checkInTime === 'string' ? meta.checkInTime : '2:00 PM';
  const checkOutTime = typeof meta.checkOutTime === 'string' ? meta.checkOutTime : '12:00 PM';
  const roomName = typeof meta.roomName === 'string' ? meta.roomName : null;
  const mealType = typeof meta.mealType === 'string' ? meta.mealType : null;
  const savedBookingCode = typeof meta.bookingCode === 'string' ? meta.bookingCode : null;
  // Per-room breakdown persisted at room-pick time (one entry per configured
  // room, PaxRooms order) - lets the card list each booked room with its pax.
  const roomsBreakdown = (Array.isArray(meta.roomsBreakdown) ? meta.roomsBreakdown : []).filter(
    (r): r is { name: string; mealType: string | null; adults: number | null; children: number } =>
      Boolean(r && typeof r === 'object' && typeof (r as { name?: unknown }).name === 'string'),
  );

  // 2026-07-07. Auto-select the first live room for a TBO hotel when the
  // operator hasn't picked one yet, so the day card shows room + meal + price
  // details from the moment the hotel is added. Guarded on:
  //  - source === 'TBO' + hotelCode + dates present (live rooms fetchable)
  //  - onUpdate available (editable card, not a preview row)
  //  - no room saved yet (checks both roomName and bookingCode - operators can
  //    still "Change Rooms & Meals" afterwards; the popup will reset on Save)
  // React Query caches the fetch, so multiple hotel cards on the same day
  // screen don't re-hit TBO per render.
  const autoDetail = useItineraryDetail(itineraryId).data ?? null;
  // This stay's occupancy: the leg's per-stay room override when set, else the
  // itinerary's global room config (see paxRoomsForStay).
  const autoPaxRooms = paxRoomsForStay(autoDetail, stayCityId ?? null, checkIn);
  const shouldAutoPickRoom = Boolean(
    editable &&
    onUpdate &&
    component.source === 'TBO' &&
    component.tboHotelCode &&
    checkIn &&
    checkOut &&
    !roomName &&
    !savedBookingCode,
  );
  // Legacy components picked a room before roomsBreakdown existed - backfill
  // the per-room list from the live options (matched by bookingCode, then
  // name) without touching the saved room, meal or price.
  const shouldBackfillBreakdown = Boolean(
    editable &&
    onUpdate &&
    component.source === 'TBO' &&
    component.tboHotelCode &&
    checkIn &&
    checkOut &&
    (roomName || savedBookingCode) &&
    roomsBreakdown.length === 0,
  );
  const autoRoomsQuery = useTboHotelRooms(
    shouldAutoPickRoom || shouldBackfillBreakdown
      ? {
          hotelCode: component.tboHotelCode as string,
          checkIn: checkIn as string,
          checkOut: checkOut as string,
          paxRooms: autoPaxRooms,
          guestNationality: 'IN',
          currencyCode: currency,
        }
      : null,
  );
  const autoPickedRef = useRef<string | null>(null);
  useEffect(() => {
    if ((!shouldAutoPickRoom && !shouldBackfillBreakdown) || !onUpdate) return;
    if (autoPickedRef.current === component.id) return;
    const rooms = autoRoomsQuery.data?.rooms ?? [];
    if (rooms.length === 0) return;
    const toBreakdown = (r: (typeof rooms)[number]): Record<string, unknown>[] =>
      r.subRooms.map((sub, i) => ({
        name: sub.name,
        mealType: sub.mealType,
        adults: autoPaxRooms[i]?.adults ?? null,
        children: autoPaxRooms[i]?.children ?? 0,
        // Bake per-child ages so a re-price can reconstruct the exact occupancy
        // (honouring a per-stay room override) instead of re-deriving pax.
        childAges: autoPaxRooms[i]?.childAges ?? [],
      }));
    if (shouldAutoPickRoom) {
      const first = rooms[0];
      if (!first) return;
      autoPickedRef.current = component.id;
      onUpdate({
        costPaise: first.pricePaise,
        metaJson: {
          ...meta,
          roomName: first.name,
          mealType: first.mealType,
          bookingCode: first.bookingCode,
          roomsBreakdown: toBreakdown(first),
        },
      });
      return;
    }
    // Backfill: match the already-saved room; leave price/booking untouched.
    const match =
      rooms.find((r) => savedBookingCode !== null && r.bookingCode === savedBookingCode) ??
      rooms.find((r) => roomName !== null && r.name === roomName);
    if (!match) return;
    autoPickedRef.current = component.id;
    onUpdate({ metaJson: { ...meta, roomsBreakdown: toBreakdown(match) } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoRoomsQuery.data,
    shouldAutoPickRoom,
    shouldBackfillBreakdown,
    component.id,
    onUpdate,
    meta,
  ]);

  // Booking pax: the rooms/travellers allocation the operator configured on
  // the itinerary - shown on the card so it's clear who the stay is booked
  // for. Falls back to the itinerary head-counts for pre-roomGroups data.
  // Rooms shown on the card: the stay's override when set, else the global
  // config (roomsForStayFull resolves both and always returns >= 1 room).
  const bookingRooms = roomsForStayFull(autoDetail, stayCityId ?? null, checkIn);
  const guestCounts = bookingRooms.length
    ? bookingRooms.reduce(
        (acc, r) => ({
          adults: acc.adults + (r.adults || 0),
          children: acc.children + (r.children || 0),
          infants: acc.infants + (r.infants || 0),
        }),
        { adults: 0, children: 0, infants: 0 },
      )
    : autoDetail
      ? {
          adults: autoDetail.adultsCount ?? 0,
          children: autoDetail.childrenCount ?? 0,
          infants: autoDetail.infantsCount ?? 0,
        }
      : null;
  const guestsLabel =
    guestCounts && guestCounts.adults > 0
      ? [
          `${guestCounts.adults} Adult${guestCounts.adults === 1 ? '' : 's'}`,
          guestCounts.children > 0
            ? `${guestCounts.children} Child${guestCounts.children === 1 ? '' : 'ren'}`
            : null,
          guestCounts.infants > 0
            ? `${guestCounts.infants} Infant${guestCounts.infants === 1 ? '' : 's'}`
            : null,
        ]
          .filter(Boolean)
          .join(', ')
      : null;
  const roomsCount = bookingRooms.length;

  // Heading shows only the hotel name. metaJson.hotelName is the clean name
  // snapshotted at add-time; legacy titles may carry a " - <room blob>" suffix.
  const hotelName =
    typeof meta.hotelName === 'string' && meta.hotelName ? meta.hotelName : component.title;

  // Live content (photo, address/landmark, rating) fetched on demand from TBO.
  const live = useTboHotelLiveDetails(fetchLiveDetails ? component.tboHotelCode : null).data;
  // 2026-07-05. Fall back to the metaJson cover photo the TBO picker
  // now writes (from the mirror-enriched live-search options) so the
  // picker cards + saved components render an image without needing
  // a second per-hotel live-details fetch.
  const photo =
    live?.photos?.[0] ??
    (typeof meta.coverPhotoUrl === 'string' ? (meta.coverPhotoUrl as string) : null);
  const landmark =
    live?.address ?? (typeof meta.address === 'string' ? (meta.address as string) : null);
  // Reverse-geocoded neighborhood ("Downtown Dubai") from Nominatim, populated
  // async on first-see for TBO hotels. Prefer the live fetch (freshest); fall
  // back to what was snapshotted onto the component when it was added.
  const neighborhood =
    live?.neighborhoodName ??
    (typeof meta.neighborhoodName === 'string' ? (meta.neighborhoodName as string) : null);
  // The two location facts on a hotel card: distance to the city's central
  // railway station and to the nearest airport. Snapshotted on metaJson when
  // the operator picks the hotel (same shape as the search payload).
  //   isDriving = true  -> OSRM route: "18 min · 5.1 km drive"
  //   isDriving = false -> straight-line until the route is computed: "~4.2 km"
  // Components saved before this change carry neither field and show no line.
  const readTransport = (
    raw: unknown,
  ): {
    name: string;
    iataCode: string | null;
    distanceKm: number;
    durationMinutes: number | null;
    isDriving: boolean;
  } | null => {
    if (!raw || typeof raw !== 'object') return null;
    const o = raw as Record<string, unknown>;
    if (typeof o.name !== 'string' || typeof o.distanceKm !== 'number') return null;
    return {
      name: o.name,
      iataCode: typeof o.iataCode === 'string' ? o.iataCode : null,
      distanceKm: o.distanceKm,
      durationMinutes: typeof o.durationMinutes === 'number' ? o.durationMinutes : null,
      isDriving: o.isDriving === true,
    };
  };
  const centralStation = readTransport(meta.centralStation);
  const nearestAirport = readTransport(meta.nearestAirport);
  const fmtKm = (km: number): string =>
    km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`;
  const fmtLeg = (t: {
    distanceKm: number;
    durationMinutes: number | null;
    isDriving: boolean;
  }): string =>
    t.isDriving && t.durationMinutes !== null
      ? `${t.durationMinutes} min · ${fmtKm(t.distanceKm)} drive`
      : `~${fmtKm(t.distanceKm)}`;
  const stars = live?.starRating ?? metaStars;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3.5 transition-shadow hover:shadow-sm">
      <div className="flex gap-3.5 max-sm:flex-wrap">
        <div className="relative h-24 w-32 shrink-0 overflow-hidden rounded-lg bg-linear-to-br from-violet-100 to-violet-50">
          {photo ? (
            <img
              src={photo}
              alt={component.title}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center">
              <BedDouble className="h-6 w-6 text-violet-400" aria-hidden />
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1 max-sm:order-last max-sm:basis-full">
          <div className="flex items-start gap-1.5">
            <p className="min-w-0 flex-1 text-sm font-semibold break-words text-zinc-900">
              {hotelName}
            </p>
            {stars !== null && stars !== undefined && stars > 0 && (
              <span
                className="inline-flex shrink-0 items-center text-amber-400"
                aria-label={`${stars} star`}
              >
                {Array.from({ length: Math.min(5, stars) }).map((_, i) => (
                  <Star key={i} className="h-3 w-3 fill-current" aria-hidden />
                ))}
              </span>
            )}
          </div>
          {landmark && (
            <p className="mt-0.5 flex items-center gap-1 truncate text-xs text-zinc-500">
              <MapPin className="h-3 w-3 shrink-0 text-zinc-400" aria-hidden />
              <span className="whitespace-normal">{landmark}</span>
            </p>
          )}
          {neighborhood && (
            <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-zinc-500">
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium tracking-wide text-emerald-700"
                title={`Located in ${neighborhood}`}
              >
                Located in {neighborhood}
              </span>
            </p>
          )}
          {(centralStation || nearestAirport) && (
            <div className="mt-1 flex flex-wrap gap-1">
              {centralStation && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-medium text-zinc-700"
                  title={
                    centralStation.isDriving
                      ? `Driving route to ${centralStation.name}`
                      : `Straight-line distance to ${centralStation.name} (driving route pending)`
                  }
                >
                  <TrainFront className="h-2.5 w-2.5 text-zinc-400" aria-hidden />
                  {fmtLeg(centralStation)} from {centralStation.name}
                </span>
              )}
              {nearestAirport && (
                <span
                  className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-1.5 py-0.5 text-[10px] font-medium text-zinc-700"
                  title={
                    nearestAirport.isDriving
                      ? `Driving route to ${nearestAirport.name}`
                      : `Straight-line distance to ${nearestAirport.name} (driving route pending)`
                  }
                >
                  <Plane className="h-2.5 w-2.5 text-zinc-400" aria-hidden />
                  {fmtLeg(nearestAirport)} from{' '}
                  {nearestAirport.iataCode ? `${nearestAirport.iataCode} · ` : ''}
                  {nearestAirport.name}
                </span>
              )}
            </div>
          )}
          {/* 2026-07-06. Layout redesigned to avoid the 5-column
              crush the previous version produced: long room names
              (e.g. "Superior Double or Twin Room, 1 Double or 2 Twin
              Beds, Private Bathroom, Garden View") were truncating
              mid-word and collapsing the meal column to zero width.
              New shape:
                Row 1: Check-in | Nights | Check-out  (compact,
                       natural width, no more max-w constraint)
                Row 2: Room + Meal (only rendered when the operator
                       picked a specific room; each block flex-1 with
                       its own truncate + tooltip, meal block hidden
                       when unavailable to avoid an "-" placeholder). */}
          <div className="mt-2 inline-flex items-stretch overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50/60 max-sm:flex max-sm:w-full">
            <div className="w-[128px] px-3 py-1.5 max-sm:w-auto max-sm:flex-1">
              <p className="flex items-center gap-1 text-[9px] font-semibold tracking-wider text-emerald-600 uppercase">
                <LogIn className="h-3 w-3" aria-hidden />
                Check-in
              </p>
              <p className="mt-0.5 text-xs font-semibold text-zinc-800">
                {checkIn ? fmtDateShort(checkIn) : '-'}
              </p>
              <p className="text-[10px] text-zinc-400 tabular-nums">{checkInTime}</p>
            </div>
            <div className="flex flex-col items-center justify-center border-x border-zinc-200 bg-white px-2.5">
              <ArrowRight className="h-3 w-3 text-zinc-300" aria-hidden />
              {nights !== null && nights !== undefined && (
                <span className="mt-0.5 text-[10px] font-medium text-zinc-500 tabular-nums">
                  {nights}N
                </span>
              )}
            </div>
            <div className="w-[128px] px-3 py-1.5 text-right max-sm:w-auto max-sm:flex-1">
              <p className="flex items-center justify-end gap-1 text-[9px] font-semibold tracking-wider text-rose-500 uppercase">
                <LogOut className="h-3 w-3" aria-hidden />
                Check-out
              </p>
              <p className="mt-0.5 text-xs font-semibold text-zinc-800">
                {checkOut ? fmtDateShort(checkOut) : '-'}
              </p>
              <p className="text-[10px] text-zinc-400 tabular-nums">{checkOutTime}</p>
            </div>
          </div>
          {roomsBreakdown.length > 0 ? (
            <div className="mt-1.5 overflow-hidden rounded-lg border border-zinc-200">
              <div className="flex items-center justify-between gap-2 bg-zinc-50/60 px-3 py-1.5">
                <p className="flex items-center gap-1 text-[9px] font-semibold tracking-wider text-violet-600 uppercase">
                  <BedDouble className="h-3 w-3" aria-hidden />
                  {roomsBreakdown.length} Room{roomsBreakdown.length === 1 ? '' : 's'}
                </p>
                {guestsLabel && (
                  <p className="flex min-w-0 items-center gap-1 truncate text-[10px] font-medium text-zinc-500">
                    <Users className="h-3 w-3 shrink-0 text-sky-500" aria-hidden />
                    <span className="truncate">{guestsLabel}</span>
                  </p>
                )}
              </div>
              <ul className="divide-y divide-zinc-100 bg-white">
                {roomsBreakdown.map((r, i) => {
                  const adults = typeof r.adults === 'number' ? r.adults : null;
                  const children = typeof r.children === 'number' ? r.children : 0;
                  const occupancyLabel =
                    adults !== null && adults > 0
                      ? `${adults} Adult${adults === 1 ? '' : 's'}${
                          children > 0 ? `, ${children} Child${children === 1 ? '' : 'ren'}` : ''
                        }`
                      : null;
                  return (
                    <li
                      key={i}
                      className="flex flex-col gap-0.5 px-3 py-1.5 sm:flex-row sm:items-center sm:gap-2"
                    >
                      <span className="bg-primary/10 text-primary inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full px-1 text-[10px] font-semibold">
                        {i + 1}
                      </span>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <p className="text-xs font-medium break-words text-zinc-800" title={r.name}>
                          {r.name}
                        </p>
                        {occupancyLabel && (
                          <p className="text-[11px] text-zinc-500 tabular-nums">
                            <span className="font-semibold">Occupancy:</span> {occupancyLabel}
                          </p>
                        )}
                      </div>
                      {r.mealType && (
                        <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                          <Utensils className="h-2.5 w-2.5" aria-hidden />
                          {r.mealType.replace(/_/g, ' ')}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            (roomName || mealType || guestsLabel) && (
              <div className="mt-1.5 flex items-stretch overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50/60">
                {roomName && (
                  <div className="min-w-0 flex-[3] px-3 py-1.5">
                    <p className="flex items-center gap-1 text-[9px] font-semibold tracking-wider text-violet-600 uppercase">
                      <BedDouble className="h-3 w-3" aria-hidden />
                      Room
                    </p>
                    <p
                      className="mt-0.5 text-xs font-medium break-words text-zinc-800"
                      title={roomName}
                    >
                      {roomName}
                    </p>
                  </div>
                )}
                {guestsLabel && (
                  <div
                    className={
                      'min-w-0 flex-1 px-3 py-1.5' +
                      (roomName ? ' border-l border-zinc-200 bg-white' : '')
                    }
                  >
                    <p className="flex items-center gap-1 text-[9px] font-semibold tracking-wider text-sky-600 uppercase">
                      <Users className="h-3 w-3" aria-hidden />
                      Guests
                    </p>
                    <p
                      className="mt-0.5 truncate text-xs font-medium text-zinc-800"
                      title={`${guestsLabel}${roomsCount > 0 ? ` · ${roomsCount} Room${roomsCount === 1 ? '' : 's'}` : ''}`}
                    >
                      {guestsLabel}
                      {roomsCount > 0 && (
                        <span className="text-zinc-500">
                          {' '}
                          · {roomsCount} Room{roomsCount === 1 ? '' : 's'}
                        </span>
                      )}
                    </p>
                  </div>
                )}
                {mealType && (
                  <div
                    className={
                      'min-w-0 flex-1 px-3 py-1.5' +
                      (roomName || guestsLabel ? ' border-l border-zinc-200 bg-white' : '')
                    }
                  >
                    <p className="flex items-center gap-1 text-[9px] font-semibold tracking-wider text-amber-600 uppercase">
                      <Utensils className="h-3 w-3" aria-hidden />
                      Meal
                    </p>
                    <p
                      className="mt-0.5 truncate text-xs font-medium text-zinc-800"
                      title={mealType}
                    >
                      {mealType.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
              </div>
            )
          )}
          <div className="mt-1.5 flex flex-wrap items-center gap-1">
            {extraMattress > 0 && <UzBadge tone="secondary">+{extraMattress} mattress</UzBadge>}
            {extraAdult > 0 && <UzBadge tone="secondary">+{extraAdult} adult</UzBadge>}
            {component.isRefundable ? (
              <UzBadge tone="success">Refundable</UzBadge>
            ) : (
              <UzBadge tone="secondary">Non-refundable</UzBadge>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 max-sm:ml-auto">
          <CardPrice
            componentId={component.id}
            costPaise={component.costPaise}
            currency={currency}
            amountClassName="text-base font-bold text-zinc-900 tabular-nums"
            caption="total stay"
            captionClassName="text-[10px] text-zinc-400"
          />
          {editable && (
            <EditRemove
              onEdit={onEdit}
              onRemove={onRemove}
              title={component.title}
              kind={component.kind}
            />
          )}
          {actionSlot}
        </div>
      </div>

      {editable && onUpdate && (
        <div className="mt-2 flex justify-end border-t border-zinc-100 pt-2">
          <UzButton
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={() => setEditRooms(true)}
          >
            <BedDouble className="mr-1 h-3 w-3" aria-hidden />
            Change Rooms & Meals
          </UzButton>
        </div>
      )}

      {editRooms && onUpdate && (
        <HotelRoomsDialog
          component={component}
          currency={currency}
          itineraryId={itineraryId}
          stayCityId={stayCityId}
          onClose={() => setEditRooms(false)}
          onSave={(patch) => {
            onUpdate(patch);
            setEditRooms(false);
          }}
        />
      )}
    </div>
  );
};

const HotelRoomsDialog = ({
  component,
  currency,
  itineraryId,
  stayCityId,
  onClose,
  onSave,
}: {
  component: ItineraryComponentOut;
  currency: string;
  itineraryId?: string;
  stayCityId?: string | null;
  onClose: () => void;
  onSave: (patch: Partial<UpdateItineraryComponentRequest>) => void;
}): React.JSX.Element => {
  const meta = readMeta(component);
  const { canSeeAmounts } = usePriceVisibility();
  // Preserved for the non-preferred (TBO) save path; TBO derives room/meal from
  // the live options, so these keep the existing values rather than being edited.
  const [roomTypeId] = useState<string | null>(component.roomTypeId);
  const [mealPlanId] = useState<string | null>(component.mealPlanId);
  const [extraMattress] = useState<number>(
    typeof meta.extraMattress === 'number' ? meta.extraMattress : 0,
  );
  const [extraAdult] = useState<number>(typeof meta.extraAdult === 'number' ? meta.extraAdult : 0);
  const [costRupees, setCostRupees] = useState<number>(
    component.costPaise ? Math.round(Number(component.costPaise) / 100) : 0,
  );
  const [chosen, setChosen] = useState<{
    name: string;
    mealType: string | null;
    bookingCode: string | null;
    isRefundable?: boolean;
    subRooms?: { name: string; mealType: string | null }[];
  } | null>(
    typeof meta.roomName === 'string'
      ? {
          name: meta.roomName as string,
          mealType: typeof meta.mealType === 'string' ? (meta.mealType as string) : null,
          bookingCode: typeof meta.bookingCode === 'string' ? (meta.bookingCode as string) : null,
        }
      : null,
  );

  // Live TBO room options for this hotel (only when it's a TBO hotel with dates).
  const detail = useItineraryDetail(itineraryId).data ?? null;
  const checkIn =
    component.startDate ?? (typeof meta.checkIn === 'string' ? (meta.checkIn as string) : null);
  const checkOut =
    component.endDate ?? (typeof meta.checkOut === 'string' ? (meta.checkOut as string) : null);
  // Occupancy for this stay: leg override when set, else the global config.
  const paxRooms = paxRoomsForStay(detail, stayCityId ?? null, checkIn);
  const canFetchRooms = Boolean(component.tboHotelCode && checkIn && checkOut);
  const roomsQuery = useTboHotelRooms(
    canFetchRooms
      ? {
          hotelCode: component.tboHotelCode as string,
          checkIn: checkIn as string,
          checkOut: checkOut as string,
          paxRooms,
          guestNationality: 'IN',
          currencyCode: currency,
        }
      : null,
  );
  const liveRooms = roomsQuery.data?.rooms ?? [];

  // ---- Preferred (in-house) hotel: config-driven room + meal auto-pricing ----
  // The occupancy already lives on the itinerary (Configure step), so the agent
  // just picks a Room type + Meal and the price is computed - no extras, no
  // manual cost. Mirrors the preferred hotel picker's engine.
  const isPreferred = detail?.hotelSource !== 'TBO' && component.source !== 'TBO';
  const prefRoomsQuery = useHotelRooms(isPreferred ? component.hotelId : null);
  const prefInvQuery = useHotelInventory(isPreferred ? component.hotelId : null, {
    page: 1,
    pageSize: 100,
    sortDir: 'asc',
    ...(checkIn ? { onDate: checkIn } : {}),
  });
  const prefRooms = useMemo(
    () => (prefRoomsQuery.data ?? []).filter((r) => r.isActive),
    [prefRoomsQuery.data],
  );
  const prefInvByRoom = useMemo(() => {
    const m = new Map<string, HotelInventoryListItem>();
    for (const inv of prefInvQuery.data?.data ?? []) {
      if (inv.hotelRoomId && !m.has(inv.hotelRoomId)) m.set(inv.hotelRoomId, inv);
    }
    return m;
  }, [prefInvQuery.data]);
  const configRooms = useMemo(
    () => roomsForStayFull(detail, stayCityId ?? null, checkIn),
    [detail, stayCityId, checkIn],
  );
  const prefRoomsAreGlobal = stayRoomsAreGlobal(detail, stayCityId ?? null, checkIn);
  const stayNights = (() => {
    if (!checkIn || !checkOut) return 1;
    const n = Math.round((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / 86_400_000);
    return Math.max(1, n);
  })();
  const [prefRoomId, setPrefRoomId] = useState<string | null>(null);
  // Selected meal plan id; null = Room Only (no supplement).
  const [prefMealPlanId, setPrefMealPlanId] = useState<string | null>(component.mealPlanId);
  // Seed the room selection once rooms load - match the saved room type.
  useEffect(() => {
    if (!isPreferred || prefRoomId || prefRooms.length === 0) return;
    const saved = prefRooms.find((r) => r.roomTypeId && r.roomTypeId === component.roomTypeId);
    setPrefRoomId(saved?.id ?? prefRooms[0]?.id ?? null);
  }, [isPreferred, prefRooms, prefRoomId, component.roomTypeId]);

  const prefRoom = prefRooms.find((r) => r.id === prefRoomId) ?? null;
  const prefInv = prefRoomId ? (prefInvByRoom.get(prefRoomId) ?? null) : null;
  const prefMealOptions = (prefInv?.mealOptions ?? []).filter((m) => m.isActive);
  const prefMeal = prefMealOptions.find((m) => m.mealPlanId === prefMealPlanId) ?? null;
  const prefPriced = prefInv
    ? priceInventoryForConfig(
        prefInv,
        prefRoom?.maxOccupancy ?? 2,
        prefMeal,
        configRooms,
        stayNights,
      )
    : {
        total: 0,
        perRoom: [] as number[],
        unavailable: prefRoom ? 'No rate card for these dates' : null,
      };
  const prefCostInr = Math.round(prefPriced.total * (prefInv?.fxRateToInr ?? 1));

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const save = (): void => {
    // Preferred: room + meal picked, price computed from the configured
    // occupancy. No extras, no manual cost. Currency stamped so the server
    // FX-converts costPaise to the itinerary's INR on save.
    if (isPreferred) {
      if (!prefRoom || !prefInv || prefPriced.unavailable) return;
      // Drop any stale weights first: they only stay when this save priced
      // the itinerary's global rooms (never a per-stay override).
      const { roomCostWeights: _staleWeights, ...metaRest } = meta as Record<string, unknown>;
      void _staleWeights;
      onSave({
        roomTypeId: prefRoom.roomTypeId ?? null,
        occupancyTypeId: prefInv.occupancyTypeId ?? prefRoom.occupancyTypeId ?? null,
        mealPlanId: prefMealPlanId,
        costPaise: String(Math.max(0, Math.round(prefPriced.total * 100))),
        metaJson: {
          ...metaRest,
          ...(prefRoomsAreGlobal ? { roomCostWeights: prefPriced.perRoom } : {}),
          provider: 'PREFERRED',
          roomName: prefRoom.name,
          roomCategory: prefRoom.roomTypeName ?? null,
          occupancyTypeName: prefRoom.occupancyTypeName ?? null,
          mealPlanName: prefMeal?.mealPlanName ?? 'Room Only',
          currencyCode: prefInv.currencyCode ?? 'INR',
        },
      });
      return;
    }
    onSave({
      roomTypeId,
      mealPlanId,
      costPaise: String(Math.max(0, Math.round(costRupees * 100))),
      ...(chosen && typeof chosen.isRefundable === 'boolean'
        ? { isRefundable: chosen.isRefundable }
        : {}),
      metaJson: {
        ...meta,
        extraMattress,
        extraAdult,
        ...(chosen
          ? {
              roomName: chosen.name,
              mealType: chosen.mealType,
              bookingCode: chosen.bookingCode,
              ...(chosen.subRooms
                ? {
                    roomsBreakdown: chosen.subRooms.map((sub, i) => ({
                      name: sub.name,
                      mealType: sub.mealType,
                      adults: paxRooms[i]?.adults ?? null,
                      children: paxRooms[i]?.children ?? 0,
                      childAges: paxRooms[i]?.childAges ?? [],
                    })),
                  }
                : {}),
            }
          : {}),
      },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/40 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[85vh] w-full max-w-md flex-col rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <header className="flex items-center justify-between border-b border-zinc-100 px-5 py-3.5">
          <h3 className="text-sm font-semibold tracking-tight text-zinc-900">Rooms & meals</h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>
        <div className="min-h-0 flex-1 space-y-4 overflow-auto px-5 py-5">
          {/* Live TBO room options */}
          {canFetchRooms && (
            <div>
              <p className="mb-2 text-[10px] font-semibold tracking-wider text-zinc-400 uppercase">
                Available rooms
              </p>
              {roomsQuery.isLoading ? (
                <div className="flex items-center gap-2 py-3 text-xs text-zinc-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Fetching rooms...
                </div>
              ) : roomsQuery.isError ? (
                <p className="text-xs text-amber-600">
                  Couldn&apos;t load live rooms; set details manually below.
                </p>
              ) : liveRooms.length === 0 ? (
                <p className="text-xs text-zinc-400">
                  No live rooms returned; set details manually.
                </p>
              ) : (
                <div className="max-h-56 space-y-1.5 overflow-auto">
                  {liveRooms.map((r, i) => {
                    const selected =
                      chosen?.bookingCode !== null &&
                      chosen?.bookingCode !== undefined &&
                      chosen.bookingCode === r.bookingCode;
                    return (
                      <button
                        key={`${r.bookingCode ?? i}`}
                        type="button"
                        onClick={() => {
                          setChosen({
                            name: r.name,
                            mealType: r.mealType,
                            bookingCode: r.bookingCode,
                            isRefundable: r.isRefundable,
                            subRooms: r.subRooms,
                          });
                          setCostRupees(Math.round(Number(r.pricePaise) / 100));
                        }}
                        className={
                          'w-full rounded-lg border p-2.5 text-left transition-colors ' +
                          (selected
                            ? 'border-primary bg-accent/60'
                            : 'border-zinc-200 hover:bg-zinc-50')
                        }
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="min-w-0 flex-1 text-xs font-medium text-zinc-900">
                            {r.name}
                          </span>
                          <div className="flex shrink-0 flex-col items-end">
                            <AdminOnlyAmount
                              costPaise={r.pricePaise}
                              currency={r.currencyCode}
                              className="text-xs font-semibold text-zinc-900 tabular-nums"
                            />
                            {/* Delta vs the currently-saved room price, matching
                                the TBO picker's room list. Makes cheaper /
                                pricier alternatives obvious at a glance without
                                the operator doing the math. */}
                            <PriceDelta
                              base={component.costPaise}
                              now={r.pricePaise}
                              currency={r.currencyCode}
                            />
                          </div>
                        </div>
                        <div className="mt-0.5 flex flex-wrap gap-1.5">
                          <MealBadge mealType={r.mealType} />
                          {r.isRefundable ? (
                            <UzBadge tone="success">Refundable</UzBadge>
                          ) : (
                            <UzBadge tone="secondary">Non-refundable</UzBadge>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Preferred (in-house) hotels: pick Room type + Meal; the price is
              computed from the configured occupancy (Configure step) via the
              rate card - no extras, no manual cost. TBO derives room+meal from
              the live options above. */}
          {isPreferred && (
            <>
              {prefRoomsQuery.isLoading || prefInvQuery.isLoading ? (
                <div className="flex items-center gap-2 py-3 text-xs text-zinc-400">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Loading rooms...
                </div>
              ) : prefRooms.length === 0 ? (
                <p className="text-xs text-amber-600">
                  This hotel has no room types configured. Add rooms + rate cards in the Hotels
                  master.
                </p>
              ) : (
                <>
                  <UzSelect
                    label="Room type"
                    value={prefRoomId ?? ''}
                    onValueChange={(v) => {
                      setPrefRoomId(v || null);
                      setPrefMealPlanId(null); // reset meal when the room changes
                    }}
                    options={prefRooms.map((r) => ({
                      value: r.id,
                      label: r.roomTypeName ?? r.name,
                    }))}
                  />
                  <UzSelect
                    label="Meal plan"
                    value={prefMealPlanId ?? ''}
                    onValueChange={(v) => setPrefMealPlanId(v || null)}
                    options={[
                      { value: '', label: 'Room Only' },
                      ...prefMealOptions.map((m) => ({
                        value: m.mealPlanId,
                        label: m.mealPlanName ?? m.mealPlanCode ?? 'Meal',
                      })),
                    ]}
                  />
                  {prefPriced.unavailable ? (
                    <p className="text-xs text-amber-600">{prefPriced.unavailable}</p>
                  ) : (
                    canSeeAmounts && (
                      <div className="flex items-baseline justify-between rounded-lg bg-zinc-50 px-3 py-2">
                        <span className="text-xs text-zinc-500">
                          Total ({configRooms.length} room{configRooms.length === 1 ? '' : 's'} ×{' '}
                          {stayNights}N)
                        </span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-zinc-900 tabular-nums">
                            ₹{prefCostInr.toLocaleString('en-IN')}
                          </span>
                          <PriceDelta
                            base={component.costPaise}
                            now={String(prefCostInr * 100)}
                            currency="INR"
                          />
                        </div>
                      </div>
                    )
                  )}
                </>
              )}
            </>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-zinc-100 px-5 py-3.5">
          <UzButton variant="outline" size="sm" onClick={onClose}>
            Cancel
          </UzButton>
          <UzButton
            size="sm"
            onClick={save}
            disabled={isPreferred && (!prefRoom || !prefInv || Boolean(prefPriced.unavailable))}
          >
            Save
          </UzButton>
        </footer>
      </div>
    </div>
  );
};

// ---- Cruise (cruise-as-stay Phase 2) ----

/** One configured room's raw occupancy, mirrors `CruiseConfigRoom` on the API
 *  side - the cabin stay reuses the trip's Rooms & travellers rather than its
 *  own occupancy config, so this is the shape `metaJson.occupancy` snapshots. */
interface CruiseOccupancyRoom {
  adults: number;
  children: number;
  childrenNoBed: number;
  infants: number;
}

const readCruiseOccupancy = (v: unknown): CruiseOccupancyRoom[] =>
  Array.isArray(v)
    ? v.filter((r): r is CruiseOccupancyRoom =>
        Boolean(r && typeof r === 'object' && 'adults' in r),
      )
    : [];

/**
 * The placed CRUISE component, rendered like the hotel stay card: ship name as
 * the heading, cabin + deck as the subtitle, check-in/check-out in the same
 * block hotels use, and the total cruise price. Mirrors the hotel card's two
 * distinct actions: "Change cruise" (the EditRemove header button, `onEdit`)
 * opens `CruiseChangeModal` listing every cruise in the embark city, while
 * "Change cabin & meals" below (`onChangeCabin`) opens the same modal locked
 * to the cruise already on the day, for a cabin/meal-plan-only swap.
 */
const CruiseCard = ({
  component,
  editable,
  onEdit,
  onRemove,
  actionSlot,
  onManualPriceSave,
  onChangeCabin,
}: ComponentCardProps): React.JSX.Element => {
  const meta = readMeta(component);
  const currency = typeof meta.currencyCode === 'string' ? meta.currencyCode : 'INR';
  const shipName =
    typeof meta.shipName === 'string' && meta.shipName ? meta.shipName : component.title;
  const cabinName = typeof meta.cabinName === 'string' ? meta.cabinName : null;
  const deck = typeof meta.deck === 'string' ? meta.deck : null;
  const boardBasis = typeof meta.boardBasis === 'string' ? meta.boardBasis : null;
  const nights = typeof meta.nights === 'number' ? meta.nights : null;
  const checkIn = component.startDate ?? (typeof meta.checkIn === 'string' ? meta.checkIn : null);
  const checkOut = component.endDate ?? (typeof meta.checkOut === 'string' ? meta.checkOut : null);
  const unavailableReason =
    typeof meta.unavailableReason === 'string' ? meta.unavailableReason : null;

  // Cover photo from the cruise master (falls back to the ship icon). Mirrors
  // the hotel card, which shows the hotel's cover.
  const cruiseDetail = useCruiseDetail(component.cruiseId ?? null);
  const coverUrl = useCruiseCoverUrl(cruiseDetail.data?.photos);

  const occupancy = readCruiseOccupancy(meta.occupancy);
  const guestCounts = occupancy.reduce(
    (acc, r) => ({
      adults: acc.adults + (r.adults || 0),
      children: acc.children + (r.children || 0) + (r.childrenNoBed || 0),
      infants: acc.infants + (r.infants || 0),
    }),
    { adults: 0, children: 0, infants: 0 },
  );
  const guestsLabel =
    guestCounts.adults > 0
      ? [
          `${guestCounts.adults} Adult${guestCounts.adults === 1 ? '' : 's'}`,
          guestCounts.children > 0
            ? `${guestCounts.children} Child${guestCounts.children === 1 ? '' : 'ren'}`
            : null,
          guestCounts.infants > 0
            ? `${guestCounts.infants} Infant${guestCounts.infants === 1 ? '' : 's'}`
            : null,
        ]
          .filter(Boolean)
          .join(', ')
      : null;
  const cabinsCount = occupancy.length;

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-3.5 transition-shadow hover:shadow-sm">
      <div className="flex gap-3.5 max-sm:flex-wrap">
        <div className="relative flex h-24 w-32 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-linear-to-br from-cyan-100 to-cyan-50">
          {coverUrl ? (
            <img src={coverUrl} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <Ship className="h-8 w-8 text-cyan-400" aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1 max-sm:order-last max-sm:basis-full">
          <p className="min-w-0 flex-1 text-sm font-semibold break-words text-zinc-900">
            {shipName}
          </p>
          {(cabinName || deck) && (
            <p className="mt-0.5 truncate text-xs text-zinc-500">
              {[cabinName, deck].filter(Boolean).join(' · ')}
            </p>
          )}
          {boardBasis && (
            <p className="mt-1">
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700">
                <Utensils className="h-2.5 w-2.5" aria-hidden />
                {boardBasis.replace(/_/g, ' ')}
              </span>
            </p>
          )}

          <div className="mt-2 inline-flex items-stretch overflow-hidden rounded-lg border border-zinc-200 bg-zinc-50/60 max-sm:flex max-sm:w-full">
            <div className="w-[128px] px-3 py-1.5 max-sm:w-auto max-sm:flex-1">
              <p className="flex items-center gap-1 text-[9px] font-semibold tracking-wider text-emerald-600 uppercase">
                <LogIn className="h-3 w-3" aria-hidden />
                Check-in
              </p>
              <p className="mt-0.5 text-xs font-semibold text-zinc-800">
                {checkIn ? fmtDateShort(checkIn) : '-'}
              </p>
            </div>
            <div className="flex flex-col items-center justify-center border-x border-zinc-200 bg-white px-2.5">
              <ArrowRight className="h-3 w-3 text-zinc-300" aria-hidden />
              {nights !== null && nights !== undefined && (
                <span className="mt-0.5 text-[10px] font-medium text-zinc-500 tabular-nums">
                  {nights}N
                </span>
              )}
            </div>
            <div className="w-[128px] px-3 py-1.5 text-right max-sm:w-auto max-sm:flex-1">
              <p className="flex items-center justify-end gap-1 text-[9px] font-semibold tracking-wider text-rose-500 uppercase">
                <LogOut className="h-3 w-3" aria-hidden />
                Check-out
              </p>
              <p className="mt-0.5 text-xs font-semibold text-zinc-800">
                {checkOut ? fmtDateShort(checkOut) : '-'}
              </p>
            </div>
          </div>

          {guestsLabel && (
            <p className="mt-1.5 flex items-center gap-1 text-[11px] text-zinc-500">
              <Users className="h-3 w-3 text-sky-500" aria-hidden />
              {guestsLabel}
              {cabinsCount > 0 && (
                <span className="text-zinc-400">
                  {' '}
                  · {cabinsCount} Cabin{cabinsCount === 1 ? '' : 's'}
                </span>
              )}
            </p>
          )}

          {unavailableReason && (
            <div className="mt-1.5 flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[11px] text-amber-800">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
              {unavailableReason}
            </div>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 max-sm:ml-auto">
          {/* Cruises are ALWAYS open-box: the seller can override the auto-
              calculated cabin price regardless of destination haul (short- or
              long-haul). The starting value is the cabin-rate cost; the saved
              override rides in the component's costPaise and survives re-pricing
              (recompute sums stored costPaise). Scoped to cruise only - every
              other component kind still gates this editor on isLongHaul. */}
          {onManualPriceSave ? (
            <InlineNetPriceEditor
              costPaise={component.costPaise}
              editable={editable}
              onSave={(next) => onManualPriceSave(component.id, next)}
            />
          ) : (
            <CardPrice
              componentId={component.id}
              costPaise={component.costPaise}
              currency={currency}
              amountClassName="text-base font-bold text-zinc-900 tabular-nums"
              caption="total cruise"
              captionClassName="text-[10px] text-zinc-400"
            />
          )}
          {editable && (
            <EditRemove
              onEdit={onEdit}
              onRemove={onRemove}
              title={component.title}
              kind={component.kind}
            />
          )}
          {actionSlot}
        </div>
      </div>

      {editable && (
        <div className="mt-2 flex justify-end border-t border-zinc-100 pt-2">
          <UzButton
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            // Opens CruiseChangeModal locked to this cruise (cabin/meal-plan
            // swap only) - mirrors the hotel "Change Rooms & Meals" button.
            // Falls back to "Change cruise" (onEdit) if the caller hasn't
            // wired onChangeCabin.
            onClick={onChangeCabin ?? onEdit}
          >
            <Utensils className="mr-1 h-3 w-3" aria-hidden />
            Change cabin & meals
          </UzButton>
        </div>
      )}
    </div>
  );
};

// ---- Generic fallback (activity / transfer / VAS) ----

const GenericRow = ({
  component,
  editable,
  onEdit,
  onRemove,
}: ComponentCardProps): React.JSX.Element => {
  const meta = KIND_META[component.kind] ?? {
    label: 'Item',
    Icon: Sparkles,
    tone: 'text-zinc-600 bg-zinc-100',
  };
  const { Icon } = meta;
  const metaJson = readMeta(component);
  const currency = typeof metaJson.currencyCode === 'string' ? metaJson.currencyCode : 'INR';
  return (
    <div className="flex items-center gap-2.5 rounded-md border border-zinc-100 px-3 py-2">
      <span className={'inline-flex h-6 w-6 items-center justify-center rounded ' + meta.tone}>
        <Icon className="h-3.5 w-3.5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1 text-sm break-words text-zinc-800">{component.title}</span>
      <CardPrice
        componentId={component.id}
        costPaise={component.costPaise}
        currency={currency}
        amountClassName="text-xs text-zinc-500 tabular-nums"
      />
      {editable && (
        <EditRemove
          onEdit={onEdit}
          onRemove={onRemove}
          title={component.title}
          kind={component.kind}
        />
      )}
    </div>
  );
};

/** Context-specific edit label per component kind ("Change flight", etc.). */
const CHANGE_LABEL: Record<string, string> = {
  FLIGHT: 'Change flight',
  HOTEL: 'Change hotel',
  ACTIVITY: 'Change activity',
  TRANSFER: 'Change transfer',
  CRUISE: 'Change cruise',
};

const EditRemove = ({
  onEdit,
  onRemove,
  title,
  kind,
}: {
  onEdit: () => void;
  onRemove: () => void;
  title: string;
  kind?: string;
}): React.JSX.Element => {
  const label = (kind && CHANGE_LABEL[kind]) || 'Edit';
  return (
    <span className="flex shrink-0 items-center gap-1">
      <button
        type="button"
        onClick={onEdit}
        className="inline-flex h-6 items-center gap-1 rounded border border-zinc-200 px-2 text-xs font-medium text-zinc-600 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
        aria-label={`${label} - ${title}`}
      >
        <Pencil className="h-3 w-3" />
        {label}
      </button>
      <button
        type="button"
        onClick={onRemove}
        className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-400 transition-colors hover:bg-red-50 hover:text-red-600"
        aria-label={`Remove ${title}`}
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </span>
  );
};

// ---- Activity row ----------------------------------------------------------

/**
 * Activity card. Single row, full width. Surfaces what the picker / variant
 * panel denormalised into metaJson at add-time: variant name, pax, transfer
 * mode, duration, category. Everything else (long description, full pricing
 * breakdown) lives behind Edit.
 */
const ActivityRow = ({
  component,
  editable,
  onEdit,
  onRemove,
  isLongHaul,
  onManualPriceSave,
}: ComponentCardProps): React.JSX.Element => {
  const meta = readMeta(component);
  const currency = typeof meta.currencyCode === 'string' ? meta.currencyCode : 'INR';
  const variantName = typeof meta.variantName === 'string' ? meta.variantName : null;
  const categoryName = typeof meta.categoryName === 'string' ? meta.categoryName : null;
  const adults = typeof meta.adults === 'number' ? meta.adults : null;
  const childCount = typeof meta.children === 'number' ? meta.children : null;
  const durationMin =
    typeof meta.tourDurationMinutes === 'number' ? meta.tourDurationMinutes : null;
  const transferInfo =
    meta.transfer && typeof meta.transfer === 'object'
      ? (meta.transfer as { mode?: string; type?: string; direction?: string })
      : null;
  const pickup =
    component.fromLocation ??
    (typeof meta.pickupLocationName === 'string' ? meta.pickupLocationName : null);
  const dropOff =
    component.toLocation ??
    (typeof meta.dropOffLocationName === 'string' ? meta.dropOffLocationName : null);
  const timeWindow = (() => {
    const s = fmtTime(component.startTime);
    const e = fmtTime(component.endTime);
    if (s && e) return `${s} - ${e}`;
    return s || e || null;
  })();
  const pax = (() => {
    if (adults === null && childCount === null) return null;
    const total = (adults ?? 0) + (childCount ?? 0);
    if ((adults ?? 0) > 0 && (childCount ?? 0) > 0) {
      return `${total} pax (${adults}A + ${childCount}C)`;
    }
    return `${total} pax`;
  })();
  const coverPhotoUrl = typeof meta.coverPhotoUrl === 'string' ? meta.coverPhotoUrl : null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3">
      <div className="flex items-start gap-3 max-sm:flex-wrap">
        {coverPhotoUrl ? (
          <div className="relative h-16 w-20 shrink-0 overflow-hidden rounded-md bg-amber-50">
            <img
              src={coverPhotoUrl}
              alt={component.title}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        ) : (
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-amber-50 text-amber-600">
            <Ticket className="h-4 w-4" aria-hidden />
          </span>
        )}
        <div className="min-w-0 flex-1 max-sm:order-last max-sm:basis-full">
          <div className="flex flex-wrap items-start gap-2">
            <span className="min-w-0 flex-1 text-sm font-medium break-words text-zinc-900">
              {component.title}
            </span>
            {variantName && (
              <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">
                {variantName}
              </span>
            )}
            {categoryName && <span className="text-[11px] text-zinc-500">{categoryName}</span>}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
            {timeWindow && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden />
                {timeWindow}
              </span>
            )}
            {durationMin !== null && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3 text-zinc-400" aria-hidden />
                {fmtDuration(durationMin)}
              </span>
            )}
            {pax && <span>{pax}</span>}
            {transferInfo?.mode && transferInfo.mode !== 'NONE' && (
              <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                <Car className="h-3 w-3" aria-hidden />
                {transferInfo.mode === 'INCLUDED'
                  ? 'Transfer incl.'
                  : `${transferInfo.mode === 'SIC' ? 'SIC' : 'Private'} transfer`}
              </span>
            )}
            {pickup && (
              <span className="inline-flex items-center gap-1 truncate">
                <MapPin className="h-3 w-3 text-zinc-400" aria-hidden />
                <span className="truncate">{pickup}</span>
              </span>
            )}
            {dropOff && pickup !== dropOff && (
              <span className="inline-flex items-center gap-1 truncate">
                <ArrowRight className="h-3 w-3 text-zinc-400" aria-hidden />
                <span className="truncate">{dropOff}</span>
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 max-sm:ml-auto">
          {isLongHaul && onManualPriceSave ? (
            <InlineNetPriceEditor
              costPaise={component.costPaise}
              editable={editable}
              onSave={(next) => onManualPriceSave(component.id, next)}
            />
          ) : (
            <CardPrice
              componentId={component.id}
              costPaise={component.costPaise}
              currency={currency}
              amountClassName="text-sm font-semibold text-zinc-900 tabular-nums"
            />
          )}
          {editable && (
            <EditRemove
              onEdit={onEdit}
              onRemove={onRemove}
              title={component.title}
              kind={component.kind}
            />
          )}
        </div>
      </div>
    </div>
  );
};

// ---- Transfer row ----------------------------------------------------------

/**
 * Transfer card. Same layout as Activity: name + denormalised vehicle / route
 * info / timing. Internal transfers (between activities within a day) live
 * here; the airport / hotel structural transfers reuse the same component but
 * are filtered out of this list upstream and rendered as fixed cards.
 */
const TransferRow = ({
  component,
  editable,
  onEdit,
  onRemove,
  isLongHaul,
  onManualPriceSave,
  dayDate,
}: ComponentCardProps): React.JSX.Element => {
  const meta = readMeta(component);
  const currency = typeof meta.currencyCode === 'string' ? meta.currencyCode : 'INR';
  const vehicleTypeName = typeof meta.vehicleTypeName === 'string' ? meta.vehicleTypeName : null;
  const transferType = typeof meta.transferType === 'string' ? meta.transferType : null;
  const transferCategory = typeof meta.transferCategory === 'string' ? meta.transferCategory : null;
  const serviceType = typeof meta.serviceType === 'string' ? meta.serviceType : null;
  // Multi-modal descriptors (null/absent for legacy car components).
  const transferMode: TransferMode =
    meta.transferMode === 'BUS' || meta.transferMode === 'TRAIN' || meta.transferMode === 'FERRY'
      ? meta.transferMode
      : 'CAR';
  const isPublic = isPublicTransferMode(transferMode);
  const modeMeta = TRANSFER_MODE_META[transferMode];
  const operatorName = typeof meta.operatorName === 'string' ? meta.operatorName : null;
  const fareClassName = typeof meta.fareClassName === 'string' ? meta.fareClassName : null;
  // Multi-day travel pass (Swiss Travel Pass / JR Pass): the picked tier's
  // validity in days, so two tiers of the same fare class stay distinguishable.
  const isMultiDayPass = meta.isMultiDayPass === true;
  const validityDays = typeof meta.validityDays === 'number' ? meta.validityDays : null;
  const fareClassLabel =
    fareClassName && validityDays ? `${fareClassName} - ${validityDays} day` : fareClassName;
  // Pass validity window: consecutive days inclusive of the start day it sits on.
  const passValidity =
    isMultiDayPass && validityDays && validityDays > 0 && dayDate
      ? { start: dayDate, end: addDaysYmd(dayDate, validityDays - 1) }
      : null;
  const vehicleCount = typeof meta.vehicleCount === 'number' ? meta.vehicleCount : null;
  const transferPax = typeof meta.pax === 'number' ? meta.pax : null;
  const seatingAdult =
    typeof meta.seatingCapacityAdult === 'number' ? meta.seatingCapacityAdult : null;
  const seatingChild =
    typeof meta.seatingCapacityChild === 'number' ? meta.seatingCapacityChild : null;
  const luggage =
    typeof meta.luggageCapacityPieces === 'number' ? meta.luggageCapacityPieces : null;
  const cityName = typeof meta.cityName === 'string' ? meta.cityName : null;
  const timeWindow = (() => {
    const s = fmtTime(component.startTime);
    const e = fmtTime(component.endTime);
    if (s && e) return `${s} - ${e}`;
    return s || e || null;
  })();
  const seating = (() => {
    if (seatingAdult === null && seatingChild === null) return null;
    if ((seatingAdult ?? 0) > 0 && (seatingChild ?? 0) > 0) {
      return `Up to ${seatingAdult}A + ${seatingChild}C`;
    }
    if ((seatingAdult ?? 0) > 0) return `Up to ${seatingAdult} pax`;
    return null;
  })();
  const coverPhotoUrl = typeof meta.coverPhotoUrl === 'string' ? meta.coverPhotoUrl : null;
  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-3">
      <div className="flex items-start gap-3 max-sm:flex-wrap">
        {coverPhotoUrl ? (
          <div className="relative h-16 w-20 shrink-0 overflow-hidden rounded-md bg-emerald-50">
            <img
              src={coverPhotoUrl}
              alt={component.title}
              className="h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          </div>
        ) : (
          <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
            <modeMeta.Icon className="h-4 w-4" aria-hidden />
          </span>
        )}
        <div className="min-w-0 flex-1 max-sm:order-last max-sm:basis-full">
          <div className="flex flex-wrap items-start gap-2">
            <span className="min-w-0 flex-1 text-sm font-medium break-words text-zinc-900">
              {component.title}
            </span>
            {/* Public modes: a mode badge (Bus/Train/Cruise). CAR keeps the
                sub-category tag; the service-type badge below stays CAR-only. */}
            {isPublic ? (
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                {modeMeta.label}
              </span>
            ) : (
              transferCategory && (
                <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-600">
                  {titleCase(transferCategory)}
                </span>
              )
            )}
            {isMultiDayPass && (
              <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
                Multi-day pass
              </span>
            )}
            {transferType && (
              <span className="text-[11px] text-zinc-500">{titleCase(transferType)}</span>
            )}
            {!isPublic && serviceType && (
              <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700">
                {serviceType === 'SIC' ? 'SIC' : 'Private'}
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
            {timeWindow && (
              <span className="inline-flex items-center gap-1">
                <Clock className="h-3 w-3" aria-hidden />
                {timeWindow}
              </span>
            )}
            {isPublic && (operatorName || fareClassLabel) && (
              <span className="inline-flex items-center gap-1">
                <modeMeta.Icon className="h-3 w-3 text-zinc-400" aria-hidden />
                {[operatorName, fareClassLabel].filter(Boolean).join(' · ')}
              </span>
            )}
            {passValidity && (
              <span className="inline-flex items-center gap-1">
                <CalendarDays className="h-3 w-3 text-zinc-400" aria-hidden />
                Valid {formatDMY(passValidity.start)} to {formatDMY(passValidity.end)} ·{' '}
                {validityDays} days
              </span>
            )}
            {vehicleTypeName && (
              <span className="inline-flex items-center gap-1">
                <Car className="h-3 w-3 text-zinc-400" aria-hidden />
                {vehicleTypeName}
                {vehicleCount && vehicleCount > 1 ? ` × ${vehicleCount}` : ''}
              </span>
            )}
            {serviceType === 'SIC' && transferPax !== null && (
              <span className="inline-flex items-center gap-1">
                <Users className="h-3 w-3 text-zinc-400" aria-hidden />
                {transferPax} pax
              </span>
            )}
            {seating && <span>{seating}</span>}
            {luggage !== null && (
              <span>
                {luggage} bag{luggage === 1 ? '' : 's'}
              </span>
            )}
            {component.fromLocation && (
              <span className="inline-flex items-center gap-1 truncate">
                <MapPin className="h-3 w-3 text-zinc-400" aria-hidden />
                <span className="truncate">{component.fromLocation}</span>
              </span>
            )}
            {component.toLocation && (
              <span className="inline-flex items-center gap-1 truncate">
                <ArrowRight className="h-3 w-3 text-zinc-400" aria-hidden />
                <span className="truncate">{component.toLocation}</span>
              </span>
            )}
            {cityName && !component.fromLocation && !component.toLocation && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3 text-zinc-400" aria-hidden />
                {cityName}
              </span>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1 max-sm:ml-auto">
          {isLongHaul && onManualPriceSave ? (
            <InlineNetPriceEditor
              costPaise={component.costPaise}
              editable={editable}
              onSave={(next) => onManualPriceSave(component.id, next)}
            />
          ) : (
            <CardPrice
              componentId={component.id}
              costPaise={component.costPaise}
              currency={currency}
              amountClassName="text-sm font-semibold text-zinc-900 tabular-nums"
            />
          )}
          {editable && (
            <EditRemove
              onEdit={onEdit}
              onRemove={onRemove}
              title={component.title}
              kind={component.kind}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Long-haul manual net-price editor. Rendered inside the price cell of the
 * activity + transfer rows when the master's rate is zero (`costPaise === 0`)
 * and the itinerary's destination is long-haul.
 *
 * Two visual states:
 *   - "editing"  - `<input>` with a save button (checkmark). Enter or click
 *                  commits; blur does not (deliberate - the input may be
 *                  focus-lost to a dropdown, drag, etc. without an intent to
 *                  commit an incomplete value).
 *   - "saved"    - the stored value with a pencil that re-opens the editor.
 *                  Fresh picks (never saved) start in "editing" with an
 *                  empty box.
 *
 * The parent's `onSave(newCostPaise)` handler is expected to persist via
 * `updateItineraryComponent` and refetch. Failure toasts live in the
 * mutation layer, not here.
 */
const InlineNetPriceEditor = ({
  costPaise,
  editable,
  onSave,
}: {
  costPaise: string;
  editable: boolean;
  onSave: (newCostPaise: string) => void | Promise<void>;
}): React.JSX.Element => {
  const saved = Number(costPaise) > 0;
  const [editing, setEditing] = useState<boolean>(!saved);
  const [draft, setDraft] = useState<string>(
    saved ? String(Math.round(Number(costPaise) / 100)) : '',
  );
  const [busy, setBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // When cost swings back to 0 (agent replaced the picked activity via the
  // picker), reset the editor to empty + editing so they must re-enter.
  useEffect(() => {
    if (!saved) {
      setDraft('');
      setEditing(true);
    }
  }, [saved]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = async (): Promise<void> => {
    const rupees = Number(draft);
    if (!Number.isFinite(rupees) || rupees <= 0) return;
    setBusy(true);
    try {
      await onSave(String(Math.round(rupees * 100)));
      setEditing(false);
    } finally {
      setBusy(false);
    }
  };

  if (!editable) {
    return (
      <p className="text-sm font-semibold text-zinc-900 tabular-nums">
        {saved ? (
          `₹${Math.round(Number(costPaise) / 100).toLocaleString('en-IN')}`
        ) : (
          <span className="text-amber-700">Net price pending</span>
        )}
      </p>
    );
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-sm font-semibold text-zinc-900 tabular-nums">
          ₹{Math.round(Number(costPaise) / 100).toLocaleString('en-IN')}
        </span>
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="inline-flex h-6 w-6 items-center justify-center rounded text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700"
          aria-label="Edit net price"
          title="Edit net price"
        >
          <Pencil className="h-3 w-3" aria-hidden />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-1 rounded-md border border-amber-300 bg-amber-50 px-1.5 py-0.5">
        <span className="text-xs text-amber-800">₹</span>
        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          min={0}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void commit();
            }
          }}
          placeholder="Net price"
          className="w-24 bg-transparent text-sm font-semibold text-zinc-900 tabular-nums outline-none placeholder:text-zinc-400"
          disabled={busy}
          aria-label="Net price in rupees"
        />
      </div>
      <button
        type="button"
        onClick={() => void commit()}
        disabled={busy || !draft || Number(draft) <= 0}
        className="inline-flex h-6 w-6 items-center justify-center rounded bg-emerald-600 text-white transition-opacity hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
        aria-label="Save net price"
        title="Save (Enter)"
      >
        {busy ? (
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
        ) : (
          <Check className="h-3 w-3" aria-hidden />
        )}
      </button>
    </div>
  );
};
